import * as Api from '@ViewModels';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { LevStandardQueryMeta, reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'template';
const SOCIAL_MEDIA_SIGNATURE_QUERY_KEY = 'socialMediaSignature';

export function useTemplate<T extends Api.ITemplate>({
	templateId,
	enabled,
	refetchOnWindowFocus,
	refetchOnMount,
	impersonationContext,
	forUserId,
	expand,
	meta,
}: {
	templateId: string;
	enabled?: boolean;
	refetchOnWindowFocus?: boolean;
	refetchOnMount?: boolean | 'always';
	impersonationContext?: Api.IImpersonationContext;
	forUserId?: string;
	expand?: 'LastUsedByDate';
	meta?: LevStandardQueryMeta;
}) {
	const userSession = useUserSession();
	return useQuery({
		enabled: enabled !== false && !!templateId,
		refetchOnWindowFocus,
		refetchOnMount,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<T>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `template/${encodeURIComponent(templateId)}`,
					queryParams: {
						...(forUserId ? { forUserId } : {}),
						...(expand ? { expand } : {}),
					},
				}),
				'GET'
			),
		queryKey: [
			BASE_QUERY_KEY,
			{ templateId, forUserId, expand, impersonationContextId: impersonationContext?.account?.id },
		],
		meta,
	});
}

export function useSocialMediaSignature({
	forUserId,
	postCreatorUserId,
	impersonationContext,
	enabled = true,
}: {
	forUserId?: string;
	postCreatorUserId?: string;
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
}) {
	const userSession = useUserSession();

	return useQuery({
		enabled,
		refetchOnWindowFocus: false,
		staleTime: 30_000,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<Api.ITemplate>({
				path: Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: 'template/socialMediaSignature',
					queryParams: forUserId ? { forUserId } : undefined,
				}),
				method: 'GET',
				throwOn404: false,
			}),
		retry: 0,
		queryKey: [
			SOCIAL_MEDIA_SIGNATURE_QUERY_KEY,
			{ forUserId, postCreatorUserId, impersonationContextId: impersonationContext?.account?.id },
		],
	});
}

export function invalidateSocialMediaSignatureQueries() {
	return reactQueryClient.invalidateQueries({ queryKey: [SOCIAL_MEDIA_SIGNATURE_QUERY_KEY] });
}

export function useTemplateQueries<T extends Api.ITemplate>({
	templateIds,
	refetchOnWindowFocus,
	queryKeyProvider,
	impersonationContext,
	queryFnOverride,
}: {
	templateIds: string[];
	refetchOnWindowFocus?: boolean;
	queryKeyProvider?: (templateId: string, index: number) => string;
	impersonationContext?: Api.IImpersonationContext;
	/** Override the default query function for fetching templates. Used by admin to fetch cross-account templates via a different endpoint. */
	queryFnOverride?: (templateId: string, userSession: Api.UserSessionContext) => Promise<T>;
}) {
	const userSession = useUserSession();
	return useQueries({
		queries: templateIds.map((templateId, index) => {
			return {
				enabled: templateId != null,
				refetchOnWindowFocus,
				queryFn: () =>
					queryFnOverride
						? queryFnOverride(templateId, userSession)
						: userSession.webServiceHelper.callAsync<T>(
								Api.ImpersonationBroker.composeApiUrl({
									impersonationContext,
									urlPath: `template/${encodeURIComponent(templateId)}`,
									queryParams: {
										expand: 'LastUsedByDate',
									},
								}),
								'GET'
							),
				queryKey: [BASE_QUERY_KEY, { templateId }, queryKeyProvider?.(templateId, index)],
			};
		}),
	});
}
